import { View, SideNavView, ContextMenu, Dialog } from 'web-mojo';
import { Location } from '@portal/models/Location.js';
import LocationFormView from '@portal/routes/LocationFormView.js';
import { Machine, MachineList } from '@portal/models/Machine.js';
import AddMachineDrawer from '@portal/routes/views/AddMachineDrawer.js';
import MachineServiceHistoryPanel from '@portal/routes/views/MachineServiceHistoryPanel.js';
import NotesPanel from '@portal/routes/views/NotesPanel.js';

// --- Machines Section --------------------------------------------------------

class LocationMachinesTab extends View {
    constructor(options = {}) {
        super(options);
        this.machines = new MachineList({ params: { location: this.model.get('id') } });
    }

    async onInit() {
        await this.machines.fetch();
    }

    async loadMachines() {
        await this.machines.fetch();
        this.render();
    }

    async onActionAddMachine() {
        const drawer = new AddMachineDrawer({ locationId: this.model.get('id') });
        Dialog.showDialog({
            title: 'Add Machine',
            body: drawer,
            size: 'lg',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Save Machine', class: 'btn-primary',
                  handler: async () => {
                      const ok = await drawer.onActionSave();
                      if (ok) this.loadMachines();
                      return ok;
                  }
                },
            ],
        });
    }

    async onActionEditMachine(event, el) {
        const id = el.getAttribute('data-id');
        const machine = this.machines.get(id);
        if (!machine) return;
        await this.getApp().showModelForm({
            model: machine,
            fields: Machine.EDIT_FORM.fields,
            title: Machine.EDIT_FORM.title,
        });
        this.loadMachines();
    }

    async onActionMachineHistory(event, el) {
        const id = el.getAttribute('data-id');
        const machine = this.machines.get(id);
        if (!machine) return;
        Dialog.showDialog({
            title: machine.get('name'),
            body: new MachineServiceHistoryPanel({ machine }),
            size: 'xl',
            footer: false,
        });
    }

    set template(v) { this._t = v; }

    get template() {
        const items = this.machines.toJSON ? this.machines.toJSON() : [];
        const rows = items.map(m => `
            <tr>
                <td>
                    <div class="fw-semibold">${m.name || '\u2014'}</div>
                    <div class="text-muted small">${m.machine_type || ''}</div>
                </td>
                <td class="text-muted small">${m.serial_number || '\u2014'}</td>
                <td>
                    ${m.device
                        ? `<span class="badge bg-success-subtle text-success"><i class="bi bi-lock-fill me-1"></i>${m.device.name}</span>`
                        : `<span class="text-muted small">No lock</span>`
                    }
                </td>
                <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                        data-action="edit-machine" data-id="${m.id}">Edit</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                        data-action="machine-history" data-id="${m.id}">History</button>
                </td>
            </tr>
        `).join('');

        return `
            <div class="py-3">
                <div class="d-flex justify-content-end mb-3">
                    <button type="button" class="btn btn-sm btn-primary" data-action="add-machine">
                        <i class="bi bi-plus-lg me-1"></i>Add Machine
                    </button>
                </div>
                ${items.length === 0
                    ? `<div class="text-center text-muted py-5">No machines at this location yet.</div>`
                    : `<table class="table table-hover align-middle mb-0">
                        <thead class="table-light">
                            <tr><th>Machine</th><th>Serial</th><th>Lock</th><th></th></tr>
                        </thead>
                        <tbody>${rows}</tbody>
                       </table>`
                }
            </div>
        `;
    }
}

// --- Main Location View ------------------------------------------------------

class LocationView extends View {
    constructor(options = {}) {
        super({
            className: 'location-view',
            ...options
        });

        this.template = `
            <div data-container="header"></div>
            <div data-container="sidenav"></div>
        `;
    }

    async onInit() {
        // Header
        this.header = new View({
            containerId: 'header',
            template: `
            <div class="d-flex align-items-start justify-content-between mb-3">
                <div class="d-flex align-items-start gap-3">
                    <div class="d-flex align-items-center justify-content-center rounded bg-light"
                         style="width:42px;height:42px;flex-shrink:0">
                        <i class="bi bi-geo-alt fs-5 text-secondary"></i>
                    </div>
                    <div>
                        <div class="d-flex align-items-center gap-2 mb-1">
                            <h5 class="mb-0">{{model.name|default('\u2014')}}</h5>
                            {{#model.is_active|bool}}
                            <span class="badge bg-success-subtle text-success">Active</span>
                            {{/model.is_active|bool}}
                            {{^model.is_active|bool}}
                            <span class="badge bg-secondary">Archived</span>
                            {{/model.is_active|bool}}
                        </div>
                        {{#model.address}}
                        <div class="text-muted small">{{model.address}}</div>
                        {{/model.address}}
                    </div>
                </div>
                <div data-container="context-menu"></div>
            </div>`
        });
        this.header.setModel(this.model);
        this.addChild(this.header);

        // Context menu
        const contextMenu = new ContextMenu({
            containerId: 'context-menu',
            className: 'context-menu-view header-menu-absolute',
            context: this.model,
            config: {
                icon: 'bi-three-dots-vertical',
                items: [
                    { label: 'Edit', action: 'edit', icon: 'bi-pencil' },
                    { type: 'divider' },
                    this.model.get('is_active') !== false
                        ? { label: 'Archive', action: 'archive', icon: 'bi-archive' }
                        : { label: 'Restore', action: 'restore', icon: 'bi-arrow-counterclockwise' },
                ]
            }
        });
        this.addChild(contextMenu);

        // Details section
        const detailsView = new View({
            model: this.model,
            template: `
            <div class="row g-3">
                <div class="col-md-6">
                    <div class="text-muted small mb-1">Coordinates</div>
                    <div class="mb-3">
                        {{#model.latitude}}{{model.latitude}}, {{model.longitude}}{{/model.latitude}}
                        {{^model.latitude}}\u2014{{/model.latitude}}
                    </div>
                    <div class="text-muted small mb-1">Timezone</div>
                    <div class="mb-3">{{model.timezone|default('\u2014')}}</div>
                    <div class="text-muted small mb-1">Requires Signature</div>
                    <div>{{{model.requires_signature|yesnoicon:'bi-check-circle-fill text-success':'bi-x-circle text-muted'}}}</div>
                </div>
                <div class="col-md-6">
                    <div class="text-muted small mb-1">Site Contact</div>
                    <div class="fw-semibold mb-1">{{model.contact_name|default('\u2014')}}</div>
                    <div class="mb-1">{{model.contact_phone|default('\u2014')}}</div>
                    <div class="mb-3">{{model.contact_email|default('\u2014')}}</div>
                    <div class="text-muted small mb-1">Access Instructions</div>
                    <div class="p-3 bg-light rounded border small" style="white-space:pre-wrap">{{model.access_instructions|default('\u2014')}}</div>
                </div>
            </div>`
        });
        detailsView.setModel(this.model);

        // Machines section
        const machinesView = new LocationMachinesTab({ model: this.model });

        // Notes section
        const notesPanel = new NotesPanel({ filter: { location: this.model.get('id') } });

        // Side nav
        this.sideNavView = new SideNavView({
            containerId: 'sidenav',
            activeSection: 'details',
            navWidth: 180,
            contentPadding: '1.25rem 2rem',
            enableResponsive: true,
            minWidth: 500,
            sections: [
                { key: 'details', label: 'Details', icon: 'bi-info-circle', view: detailsView },
                { key: 'machines', label: 'Machines', icon: 'bi-hdd-network', view: machinesView },
                { key: 'notes', label: 'Notes', icon: 'bi-journal-text', view: notesPanel },
            ]
        });
        this.addChild(this.sideNavView);
    }

    async onActionEdit() {
        const form = new LocationFormView({ model: this.model });
        Dialog.showDialog({
            title: 'Edit Location',
            body: form,
            size: 'lg',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Save Location', class: 'btn-primary',
                  handler: () => form.onActionSave()
                },
            ],
        });
        form.on('saved', (data) => {
            this.model.set(data);
            this.render();
        });
    }

    async onActionArchive() {
        const confirmed = await Dialog.confirm({
            title: 'Archive Location',
            message: `Archive "${this.model.get('name')}"? It will no longer appear in active lists.`,
            confirmText: 'Archive',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;
        const resp = await this.model.save({ is_active: false });
        if (resp?.success) {
            app.toast.success('Location archived');
            this.render();
        } else {
            app.toast.error('Failed to archive location');
        }
    }

    async onActionRestore() {
        const confirmed = await Dialog.confirm({
            title: 'Restore Location',
            message: `Restore "${this.model.get('name')}"? It will appear in active lists again.`,
            confirmText: 'Restore',
            confirmClass: 'btn-success',
        });
        if (!confirmed) return;
        const resp = await this.model.save({ is_active: true });
        if (resp?.success) {
            app.toast.success('Location restored');
            this.render();
        } else {
            app.toast.error('Failed to restore location');
        }
    }

    _onModelChange() {
        // suppress automatic re-render on model change
    }
}

Location.VIEW_CLASS = LocationView;

export default LocationView;
